import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    bpDetailsRefetchTick,
    clearBPDetailsRefetch,
    requestBPManagementPanelOpen,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import {
    isOMBestPracticeFeatureEnabled,
    isSmartProjectCopyEnabled,
} from 'shared/utils/bestPracticeUtils'
import { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { BestPracticeSyncAction } from '../../smartProjectCopy/types'
import { BP_STYLING, BP_TEST_IDS } from '../constants'
import { revertBPPreviewAndDeselect } from '../hooks/useApplyBPFromConfig'
import { useBestPracticeDetails } from '../hooks/useBestPracticeDetails'
import { useBestPracticesData } from '../hooks/useBestPracticesData'
import { useBestPracticesDisplay } from '../hooks/useBestPracticesDisplay'
import { useBestPracticesSyncStatus } from '../hooks/useBestPracticesSyncStatus'
import { useShouldRenderBPIcon } from '../hooks/useShouldRenderBPIcon'
import { BPTooltipContent, countParameterRows } from './BPTooltipContent'

const COUNT_PILL_BG_COLOR = '#0D7B55'
const COUNT_PILL_TEXT_COLOR = '#FFFFFF'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: BP_STYLING.ICON_MARGIN_LEFT,
        verticalAlign: 'middle',
        '& .bpIconContainer': {
            display: 'inline-flex',
            alignItems: 'center',
        },
    },
    hidden: {
        display: 'none',
    },
    BPIcons: {
        minWidth: theme.spacing(BP_STYLING.ICON_MIN_WIDTH_SPACING),
        lineHeight: 0,
        fontSize: pxToRem(BP_STYLING.ICON_FONT_SIZE_PX),
        textAlign: 'center',
        color: BP_STYLING.ICON_COLOR,
        position: 'absolute',
        right: -theme.spacing(2),
        paddingTop: theme.spacing(BP_STYLING.ICON_PADDING_TOP_SPACING),
        zIndex: BP_STYLING.ICON_Z_INDEX,
    },
    // Layout used only by the OM-flag BP Management workflow: pin icons to
    // the row's right edge (flex-end) so they hug the pane boundary without
    // overflowing it. The count badge is absolute-positioned (see countPill),
    // so it tracks the BP icon and doesn't consume inline space — meaning the
    // container only needs to fit the two icons.
    BPIconsWithCount: {
        right: 0,
        justifyContent: 'flex-end',
        gap: pxToRem(4),
    },
    // Anchor for the absolute-positioned count badge so the count doesn't
    // consume inline space and collide with the GBP icon when both are shown.
    bpIconWithCount: {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
    },
    countPill: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COUNT_PILL_BG_COLOR,
        color: COUNT_PILL_TEXT_COLOR,
        fontSize: pxToRem(11),
        fontWeight: 600,
        lineHeight: 1,
        minWidth: pxToRem(16),
        height: pxToRem(16),
        padding: `0 ${pxToRem(3)}`,
        borderRadius: '50%',
        position: 'absolute',
        top: pxToRem(-6),
        right: pxToRem(-4),
        pointerEvents: 'none',
    },
    countSpinner: {
        color: COUNT_PILL_BG_COLOR,
        position: 'absolute',
        top: pxToRem(-6),
        right: pxToRem(-4),
        pointerEvents: 'none',
    },
}))

interface BestPracticesIconWrapperProps {
    /** The content item to display BP icons for */
    contentItem: Partial<ContentItemViewNodesType>
    /** Additional CSS class names */
    className?: string
    /** Project element node ID for sync checking */
    projectElementNodeId?: string
}

export const BestPracticesIconWrapper = memo<BestPracticesIconWrapperProps>(
    ({ contentItem, className, projectElementNodeId }) => {
        const classes = useStyles()
        const selectedProjectElementNode = useSelectedProjectElementNode()
        const { projectId } = useParams<{ projectId: string }>()
        const smartProjectCopyEnabled = isSmartProjectCopyEnabled()
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
        const isBPManagementEnabled =
            isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace

        const shouldRenderBPIcon = useShouldRenderBPIcon(contentItem)
        const { shouldDisplayBPIcon } = useBestPracticesDisplay(contentItem)

        const { isBestPractice, isGlobalBestPractice } = useBestPracticesData(
            contentItem
        )

        // Tree nodes pass a DataNode (uses `key`); other callers pass a
        // ContentItemViewNodes (uses `id`) — accept either.
        const contentIdForBPLookup =
            (contentItem as { key?: string })?.key ?? contentItem?.id
        // Read the per-content-item tick so the apply hook can force a refresh
        // after a Set/Modify/Remove without unmounting the wrapper.
        const refetchTickMap = useReactiveVar(bpDetailsRefetchTick)
        const refetchTick = contentIdForBPLookup
            ? refetchTickMap[contentIdForBPLookup]
            : undefined
        // Prune this item's tick entry when the wrapper unmounts so the
        // bpDetailsRefetchTick map doesn't grow unboundedly across the session.
        useEffect(() => {
            if (!contentIdForBPLookup) return
            return () => clearBPDetailsRefetch(contentIdForBPLookup)
        }, [contentIdForBPLookup])

        // Eager-fetch on mount so the count badge + tooltip data are ready
        // immediately. Only the new-OM render path consumes bpDetails, so
        // skip the fetch when the OM management flag is off (AE workspace
        // and OM-off-in-OM-workspace fall back to legacy icons that don't
        // read this data).
        const {
            syncActionType,
            isBPOutOfSync,
            isGBPOutOfSync,
            isLoading,
            handleSync,
            checkIfBestPracticeIsOutOfSync,
        } = useBestPracticesSyncStatus({
            contentItem,
            projectElementNodeId,
            enabled: smartProjectCopyEnabled && shouldDisplayBPIcon,
        })

        const bpDetails = useBestPracticeDetails(
            contentIdForBPLookup,
            projectId,
            {
                fetchOnMount: isBestPractice && isBPManagementEnabled,
                refetchTick,
                useMultiple: isBPManagementEnabled,
                onBPDataFetched:
                    smartProjectCopyEnabled && shouldDisplayBPIcon
                        ? checkIfBestPracticeIsOutOfSync
                        : undefined,
            }
        )
        const paramRowCount = countParameterRows(bpDetails.data)

        if (!shouldRenderBPIcon) {
            return null
        }

        const isBestPracticeSynced =
            isBestPractice &&
            syncActionType.action === BestPracticeSyncAction.None
        const isGlobalBestPracticeSynced =
            isGlobalBestPractice &&
            syncActionType.action === BestPracticeSyncAction.None

        if (!isBPManagementEnabled) {
            return (
                <span
                    className={clsx(
                        classes.container,
                        className,
                        classes.BPIcons
                    )}
                    data-testid={BP_TEST_IDS.ICON_WRAPPER}
                >
                    {smartProjectCopyEnabled ? (
                        <BestPracticesIcons
                            isBestPractice={isBestPractice}
                            isGlobalBestPractice={isGlobalBestPractice}
                            isBestPracticeOutOfSync={isBPOutOfSync}
                            isGlobalBestPracticeOutOfSync={isGBPOutOfSync}
                            isBestPracticeSyncing={isLoading && isBestPractice}
                            isGlobalBestPracticeSyncing={
                                isLoading && isGlobalBestPractice
                            }
                            isBestPracticeSynced={isBestPracticeSynced}
                            isGlobalBestPracticeSynced={
                                isGlobalBestPracticeSynced
                            }
                            handleOutOfSyncIconClick={handleSync}
                            syncActionType={syncActionType}
                        />
                    ) : (
                        <BestPracticesIcons
                            isBestPractice={isBestPractice}
                            isGlobalBestPractice={isGlobalBestPractice}
                        />
                    )}
                </span>
            )
        }

        const handleFilterClick = useCallback(() => {
            revertBPPreviewAndDeselect()
            requestBPManagementPanelOpen(contentIdForBPLookup)
        }, [contentIdForBPLookup])

        const renderOMSyncedBPIconWithTooltip = () => (
            <HoverTooltip
                text={
                    <BPTooltipContent
                        data={bpDetails.data}
                        loading={bpDetails.loading}
                        error={bpDetails.error}
                        onFilterClick={handleFilterClick}
                    />
                }
            >
                <span className={classes.bpIconWithCount}>
                    <BestPracticesIcons
                        isBestPractice={isBestPractice}
                        isBestPracticeSynced={true}
                        disableBPTooltip={true}
                    />
                    {bpDetails.loading ? (
                        <CircularProgress
                            size={12}
                            thickness={5}
                            className={classes.countSpinner}
                            data-testid="dvBPCountSpinner"
                        />
                    ) : (
                        paramRowCount > 0 && (
                            <span className={classes.countPill}>
                                {paramRowCount}
                            </span>
                        )
                    )}
                </span>
            </HoverTooltip>
        )

        const renderBPIconWithSPCFeature = () =>
            smartProjectCopyEnabled ? (
                <BestPracticesIcons
                    isBestPractice={isBestPractice}
                    isGlobalBestPractice={isGlobalBestPractice}
                    isBestPracticeOutOfSync={isBPOutOfSync}
                    isGlobalBestPracticeOutOfSync={isGBPOutOfSync}
                    isBestPracticeSyncing={isLoading && isBestPractice}
                    isGlobalBestPracticeSyncing={isLoading && isGlobalBestPractice}
                    isBestPracticeSynced={isBestPracticeSynced}
                    isGlobalBestPracticeSynced={isGlobalBestPracticeSynced}
                    handleOutOfSyncIconClick={handleSync}
                    syncActionType={syncActionType}
                />
            ) : (
                <BestPracticesIcons
                    isBestPractice={isBestPractice}
                    isGlobalBestPractice={isGlobalBestPractice}
                />
            )

        const gbpIcon = isGlobalBestPractice && (
            <BestPracticesIcons
                isBestPractice={false}
                isGlobalBestPractice={true}
                isGlobalBestPracticeOutOfSync={
                    smartProjectCopyEnabled && isGBPOutOfSync
                }
                isGlobalBestPracticeSyncing={
                    smartProjectCopyEnabled && isLoading && isGlobalBestPractice
                }
                isGlobalBestPracticeSynced={
                    smartProjectCopyEnabled && isGlobalBestPracticeSynced
                }
                handleOutOfSyncIconClick={
                    smartProjectCopyEnabled ? handleSync : undefined
                }
                syncActionType={syncActionType}
            />
        )

        return (
            <span
                className={clsx(
                    classes.container,
                    className,
                    classes.BPIcons,
                    classes.BPIconsWithCount
                )}
                data-testid={BP_TEST_IDS.ICON_WRAPPER}
            >
                {isBestPractice &&
                    (isBPManagementEnabled &&
                    (isBestPracticeSynced || isGlobalBestPracticeSynced)
                        ? renderOMSyncedBPIconWithTooltip()
                        : renderBPIconWithSPCFeature())}
                {gbpIcon}
            </span>
        )
    }
)

BestPracticesIconWrapper.displayName = 'BestPracticesIconWrapper'
